/**
 * Type chip for a wallet ledger row (ENG-27315) — how the money entered or
 * left the wallet, at a glance.
 *
 * Styled to match PaymentTypeBadge so the Payments and Wallet surfaces read as
 * one system. The category itself comes from `paymentTypeCategory`, keeping the
 * mapping pure and testable; only colour and icon live here.
 */
import { Box, Typography } from "@mui/material";
import {
  ReceiptLong,
  Payments,
  SwapHoriz,
  Undo,
  NoteAlt,
  HelpOutline,
  AssignmentReturn,
} from "@mui/icons-material";

import type { WalletTxnCategory } from "../utils/walletLedger";

type CategoryMeta = {
  color: string;
  bgColor: string;
  IconComponent: React.ElementType;
};

/** Credits lean green/blue, debits amber/red, so direction reads from colour too. */
const CATEGORY_META: Record<WalletTxnCategory, CategoryMeta> = {
  invoice: { color: "#1976D2", bgColor: "#E3F2FD", IconComponent: ReceiptLong },
  credit_note: { color: "#673AB7", bgColor: "#EDE7F6", IconComponent: NoteAlt },
  po_advance: { color: "#2E7D32", bgColor: "#E8F5E9", IconComponent: Payments },
  // A debit undoing the advance credit: the reversal's amber and undo icon.
  po_advance_reversal: { color: "#B26A00", bgColor: "#FFF8E1", IconComponent: Undo },
  advance_adjustment: { color: "#B26A00", bgColor: "#FFF8E1", IconComponent: SwapHoriz },
  advance_adjustment_reversal: { color: "#B26A00", bgColor: "#FFF8E1", IconComponent: Undo },
  payment_request: { color: "#3655F2", bgColor: "#EBEEFE", IconComponent: Payments },
  // Open-advance refund: a credit since ENG-27743 (the vendor's transfer brings
  // the payable back up), in teal so it never reads as a PO-advance chip.
  open_advance_refund: { color: "#00796B", bgColor: "#E0F2F1", IconComponent: AssignmentReturn },
  // Credit-note refund by UTR (ENG-28637): the same "vendor sent money back"
  // credit, so the same chip.
  credit_note_refund: { color: "#00796B", bgColor: "#E0F2F1", IconComponent: AssignmentReturn },
  other: { color: "#757575", bgColor: "#F5F5F5", IconComponent: HelpOutline },
};

interface WalletTypeBadgeProps {
  category: WalletTxnCategory;
  label: string;
}

export default function WalletTypeBadge({ category, label }: WalletTypeBadgeProps) {
  const { color, bgColor, IconComponent } = CATEGORY_META[category] ?? CATEGORY_META.other;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        pl: "6px",
        pr: "10px",
        py: "4px",
        borderRadius: "100px",
        color,
        backgroundColor: bgColor,
        whiteSpace: "nowrap",
      }}
    >
      <IconComponent sx={{ fontSize: 12 }} />
      <Typography
        component="span"
        sx={{ color: "inherit", fontSize: "11px", fontWeight: 500, lineHeight: "14px" }}
      >
        {label}
      </Typography>
    </Box>
  );
}
